import { Plus, Trash2 } from 'lucide-react';
import type { JSX } from 'react';
import type { FieldErrors, UseFieldArrayReturn, UseFormRegister } from 'react-hook-form';
import { PageSection } from '@/components/layout/page.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table.js';
import type { BundleFormValues } from './bundle-form.js';

/**
 * Lignes de composition d'un bundle (`02` §8) : SKU, quantité, prix unitaire
 * optionnel. Extrait du formulaire pour que la fiche reste lisible ; reçoit
 * directement les primitives `react-hook-form` du parent plutôt que de
 * dupliquer un état.
 *
 * Une dernière ligne ne peut pas être retirée : un bundle sans composant n'a
 * pas de sens et le schéma partagé le refuserait de toute façon.
 */
export interface BundleComponentsTableProps {
  register: UseFormRegister<BundleFormValues>;
  fieldArray: UseFieldArrayReturn<BundleFormValues, 'components'>;
  errors: FieldErrors<BundleFormValues>;
}

/** Message de validation sous le champ d'une cellule. */
function CellError({ message }: { message: string | undefined }): JSX.Element | null {
  return message ? <p className="mt-1.5 text-xs text-status-red-fg">{message}</p> : null;
}

export function BundleComponentsTable({
  register,
  fieldArray,
  errors,
}: BundleComponentsTableProps): JSX.Element {
  const { fields, append, remove } = fieldArray;

  return (
    <PageSection
      title="Composants"
      flush
      actions={
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            append({ sku: '', qty: 1, unitPrice: null, order: fields.length });
          }}
        >
          <Plus aria-hidden="true" />
          Ajouter un composant
        </Button>
      }
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10 text-right">#</TableHead>
            <TableHead>SKU</TableHead>
            <TableHead className="w-28">Quantité</TableHead>
            <TableHead className="w-52">Prix unitaire</TableHead>
            <TableHead className="w-12">
              <span className="sr-only">Actions</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {fields.map((field, index) => {
            const rowErrors = errors.components?.[index];
            return (
              <TableRow key={field.id} className="hover:bg-transparent">
                <TableCell className="align-top pt-4 text-right tabular-nums text-muted-foreground">
                  {index + 1}
                </TableCell>
                <TableCell className="align-top">
                  <Input
                    placeholder="SKU"
                    className="min-w-40"
                    aria-label={`SKU du composant ${String(index + 1)}`}
                    aria-invalid={rowErrors?.sku ? true : undefined}
                    {...register(`components.${index}.sku` as const)}
                  />
                  <CellError message={rowErrors?.sku?.message} />
                </TableCell>
                <TableCell className="align-top">
                  <Input
                    type="number"
                    min={1}
                    className="w-24"
                    aria-label={`Quantité du composant ${String(index + 1)}`}
                    aria-invalid={rowErrors?.qty ? true : undefined}
                    {...register(`components.${index}.qty` as const, { valueAsNumber: true })}
                  />
                  <CellError message={rowErrors?.qty?.message} />
                </TableCell>
                <TableCell className="align-top">
                  <Input
                    placeholder="Prix unitaire (optionnel)"
                    className="w-48"
                    aria-label={`Prix unitaire du composant ${String(index + 1)}`}
                    aria-invalid={rowErrors?.unitPrice ? true : undefined}
                    {...register(`components.${index}.unitPrice` as const, {
                      setValueAs: emptyToNull,
                    })}
                  />
                  <CellError message={rowErrors?.unitPrice?.message} />
                </TableCell>
                <TableCell className="align-top text-right">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Supprimer le composant ${String(index + 1)}`}
                    disabled={fields.length <= 1}
                    onClick={() => {
                      remove(index);
                    }}
                  >
                    <Trash2 aria-hidden="true" />
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {typeof errors.components?.message === 'string' ? (
        <p className="border-t border-border px-4 py-3 text-xs text-status-red-fg">
          {errors.components.message}
        </p>
      ) : null}
    </PageSection>
  );
}

/**
 * Chaîne vide → `null` pour `unitPrice` (`decimalStringSchema.nullable()`,
 * `02` §8 : « prix unitaire optionnel »), sans passer par un nombre JS pour ne
 * pas perdre de précision.
 *
 * RHF appelle `setValueAs` aussi à l'attache initiale du `ref`, avec la valeur
 * par défaut — qui peut déjà être `null` : la valeur n'est donc pas toujours
 * une chaîne.
 */
function emptyToNull(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  return value.trim() === '' ? null : value;
}
